import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PDFDownloadLink } from "@react-pdf/renderer";
import "../../styles/mapStyles/Popup.css";
import SoilAndFertilizerReportPDF from "./SoilAndFertilizerReportPDF";
import RemoveMarkerBtn from "./SoilPopupContentComponents/RemoveMarkerBtn";
import GetSoilInfoBtn from "./SoilPopupContentComponents/GetSoilInfoBtn";
import SoilInfoLoadingIndicator from "./SoilPopupContentComponents/SoilInfoLoadingIndicator";
import SoilHealthStatus from "./SoilPopupContentComponents/SoilHealthStatus";
import GeneralRecommendation from "./SoilPopupContentComponents/GeneralRecommendation";
import CropBasedFertilizerRecommendation from "./SoilPopupContentComponents/CropBasedFertilizerRecommendation";
// import PredictionConfidence from "./SoilPopupContentComponents/PredictionConfidence";

// ─────────────────────────────────────────────
// SoilPopupContent
// Shared popup body for soil information.
//
// Handles three cases:
// 1. No Data → shows error message
// 2. Exact Match → data from a known sample location
// 3. Estimated → data blended from nearby samples
// ─────────────────────────────────────────────

function SoilPopupContent({
  placeName, // passed through to the PDF report
  coordinates, // passed through to the PDF report
  soilData,
  recommendation,
  loading,
  error,
  disableGetInfo,
  onGetSoilInfo,
  isClickMarker, // optional — only ClickMarker passes this
  onRemoveMarker, // optional — only ClickMarker passes this
  isExactMatch, // TRUE if data is from exact sample
  confidenceScore, // only shown if NOT exact match
  confidenceInterpretation, // only shown if NOT exact match
  pointsUsed, // only shown if NOT exact match
}) {
  // Tracks what the farmer has selected for automatic crop-based recommendation
  const [selectedCrop, setSelectedCrop] = useState("");
  const [selectedBlend, setSelectedBlend] = useState("");
  // Holds the crop fertilizer recommendation result after the button is clicked
  const [cropRecommendation, setCropRecommendation] = useState(null);

  // OFRA calculator state — stores the user's manual fertilizer calculation inputs
  // These are used when the farmer chooses the manual OFRA mode instead of automatic crop selection
  const [ofraContainerValue, setOfraContainerValue] = useState("");
  const [ofraFertilizerTypeValue, setOfraFertilizerTypeValue] = useState("");
  const [ofraFertilizerRateValue, setOfraFertilizerRateValue] = useState("");
  const [ofraApplicationMethodValue, setOfraApplicationMethodValue] = useState("");

  // Filename for the downloaded PDF report
  const name = "Soil Status, Management and Fertilizer Recommendation Report";
  const time = new Date().toTimeString().split(" ")[0].replace(/:/g, "-");
  const fileName = `${name} - ${time}.pdf`;
  const { t } = useTranslation();

  return (
    <>
      {/* ── Fetch button — hidden once data has been loaded ── */}
      {!disableGetInfo && (
        <div className="popup-actions">
          <GetSoilInfoBtn getSoilInfo={onGetSoilInfo} loading={loading} />

          {/* Remove marker button — only shown in ClickMarker */}
          {isClickMarker && !loading && <RemoveMarkerBtn onRemoveMarker={onRemoveMarker} />}
        </div>
      )}

      {/* ── Loading spinner ── */}
      {loading && <SoilInfoLoadingIndicator />}

      {/* ── Error message ── */}
      {error && <div className="error-message">⚠️ {error}</div>}

      {/* ── Soil results — only shown after a successful fetch ── */}
      {soilData && !loading && !error && (
        <>
          {/* Remove marker button — only shown in ClickMarker */}
          {isClickMarker && <RemoveMarkerBtn onRemoveMarker={onRemoveMarker} />}

          <div className="soil-results">
            {/* ── Section 1: Soil Health Status ── */}
            <SoilHealthStatus recommendation={recommendation} soilData={soilData} />

            {/* ── Confidence indicator (only for estimated data) ── */}
            {/* {!isExactMatch && confidenceScore !== null && (
              <PredictionConfidence
                confidenceInterpretation={confidenceInterpretation}
                pointsUsed={pointsUsed}
                confidenceScore={confidenceScore}
              />
            )}  */}

            {/* ── Section 2: General Recommendation ── */}
            <GeneralRecommendation recommendation={recommendation} />

            {/* ── Section 3: Crop Based Fertilizer Recommendation ── */}
            <CropBasedFertilizerRecommendation
              recommendation={recommendation}
              selectedCrop={selectedCrop}
              setSelectedCrop={setSelectedCrop}
              setCropRecommendation={setCropRecommendation}
              cropRecommendation={cropRecommendation}
              selectedBlend={selectedBlend}
              setSelectedBlend={setSelectedBlend}
              ofraContainerValue={ofraContainerValue}
              setOfraContainerValue={setOfraContainerValue}
              ofraFertilizerTypeValue={ofraFertilizerTypeValue}
              setOfraFertilizerTypeValue={setOfraFertilizerTypeValue}
              ofraFertilizerRateValue={ofraFertilizerRateValue}
              setOfraFertilizerRateValue={setOfraFertilizerRateValue}
              ofraApplicationMethodValue={ofraApplicationMethodValue}
              setOfraApplicationMethodValue={setOfraApplicationMethodValue}
            />

            {/* ── Download PDF — always visible once soil data is loaded ── */}
            <PDFDownloadLink
              document={
                <SoilAndFertilizerReportPDF
                  placeName={placeName}
                  coordinates={coordinates}
                  soilData={soilData}
                  recommendation={recommendation}
                  selectedCrop={selectedCrop}
                  selectedBlend={selectedBlend}
                  cropRecommendation={cropRecommendation}
                  ofraContainerValue={ofraContainerValue}
                  ofraFertilizerTypeValue={ofraFertilizerTypeValue}
                  ofraFertilizerRateValue={ofraFertilizerRateValue}
                  ofraApplicationMethodValue={ofraApplicationMethodValue}
                />
              }
              fileName={fileName}
            >
              {({ loading: pdfLoading }) => (
                <button
                  type="button"
                  className="download-report-btn"
                  style={{ marginTop: "1rem" }}
                  aria-label={pdfLoading ? t("soil.downloadingReport") : t("soil.downloadReport")}
                >
                  {pdfLoading ? (
                    t("soil.downloadingReport")
                  ) : (
                    <>
                      <span className="download-report-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" focusable="false">
                          <path
                            d="M5 20h14v-2H5v2zm7-18l-5 5h3v6h4v-6h3l-5-5z"
                            fill="currentColor"
                          />
                        </svg>
                      </span>
                      <span>{t("soil.downloadReport")}</span>
                    </>
                  )}
                </button>
              )}
            </PDFDownloadLink>
          </div>
        </>
      )}
    </>
  );
}

export default SoilPopupContent;
